// Turbo Drive stores its snapshot-restoration pointer in `history.state.turbo`
// (`{ restorationIdentifier, restorationIndex }`). It reads that pointer on
// `popstate` to decide how to restore the page the user navigated Back to.
//
// TanStack Router (mounted by AppRouter on every SPA section — events, orders,
// products, …) rewrites `history.state` via `replaceState` on mount and on
// every in-place search/filter change, writing only its own keys
// (`__TSR_index`, `key`, `__TSR_key`). That silently drops the `turbo` key from
// the current entry. So when the user later presses browser Back onto a
// TanStack-managed page (e.g. the events list, reached from the standalone
// event edit page), Turbo finds no restoration pointer and cannot restore that
// page's snapshot — it comes back blank / broken instead of restored.
//
// Fix: wrap `history.replaceState` so it carries forward an existing `turbo`
// key when the incoming state omits one. This keeps the current entry's Turbo
// restoration pointer intact across TanStack's state writes, without touching
// TanStack's own keys. We deliberately do NOT patch `pushState`: a push creates
// a *new* history position that Turbo has no snapshot for, so copying a stale
// `turbo` pointer onto it would be wrong.

let installed = false;
let nativeReplaceState: History['replaceState'] | null = null;

export function preserveTurboHistoryState(): void {
  if (installed || typeof window === 'undefined' || !window.history) return;
  installed = true;

  const original = window.history.replaceState.bind(window.history);
  nativeReplaceState = original;

  window.history.replaceState = function (
    state: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    const prevTurbo = (window.history.state as { turbo?: unknown } | null)?.turbo;

    // Only merge when Turbo state exists on the current entry AND the caller
    // isn't itself setting `turbo` (never clobber Turbo's own writes).
    const shouldPreserve =
      prevTurbo != null &&
      state != null &&
      typeof state === 'object' &&
      !('turbo' in (state as Record<string, unknown>));

    const nextState = shouldPreserve
      ? { ...(state as Record<string, unknown>), turbo: prevTurbo }
      : state;

    return original(nextState, unused, url);
  };
}

// Test-only: restore the native method and allow re-installation.
export function __resetForTests(): void {
  if (nativeReplaceState && typeof window !== 'undefined' && window.history) {
    window.history.replaceState = nativeReplaceState;
  }
  installed = false;
  nativeReplaceState = null;
}
